import React, { useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { Combobox, Listbox } from '@superops/ui-design-system';
import { isEmpty } from 'lodash';
import { getTranslatedText } from '@nile/components';
import { FormattedMessage, useIntl } from 'react-intl';

const isToday = require('dayjs/plugin/isToday');

dayjs.extend(isToday);

interface Option {
  label: string;
  value: number;
  inputValue: string;
}

type Options = Option[];

type TimeUnitType = 'minutes' | 'hours';

interface DurationDropdownProps {
  date: Date;
  endTime: Date;
  startTime: Date;
  duration: number;
  onChange: (value: number) => void;
  disabled?: boolean;
  placeholder?: string;
  maxDuration?: number;
}

function convertToHoursAndMinutes(
  timeInMinutes: number
): { hours: number; minutes: number } {
  const hours = parseInt(Math.floor(timeInMinutes / 60).toString());
  const minutes = Math.round(timeInMinutes % 60);
  return { hours, minutes };
}

function DurationDropdown({
  duration,
  date,
  endTime,
  startTime,
  onChange,
  disabled,
  placeholder,
  maxDuration
}: DurationDropdownProps): React.ReactElement {
  const Options: Options = [];
  const intl = useIntl();
  const comboboxRef = useRef();
  const [durationInput, setDurationInput] = useState<Option | null>(null);
  const [filteredOptions, setFilteredOptions] = useState<Option[]>([]);

  function translateTimeUnit(timeUnit: TimeUnitType, time: number): string {
    switch (timeUnit) {
      case 'minutes':
        return ` ${getTranslatedText(intl, 'timer.duration.unit.minutes', {
          durationMins: time
        })}`;
      case 'hours':
      default:
        return getTranslatedText(intl, 'timer.duration.unit.hours', {
          durationHrs: time
        });
    }
  }

  let newDate = dayjs(date ?? new Date())
    .startOf('date')
    .startOf('hour');

  // const maximumDuration = endTime ? dayjs(endTime).hour() : 24;
  let maximumDurationInMinutes = maxDuration ? maxDuration * 60 : 24 * 60;
  if (endTime) {
    maximumDurationInMinutes =
      dayjs(endTime).hour() * 60 + dayjs(endTime).minute();
  }
  if (!endTime && startTime) {
    const timeToDeduct =
      dayjs(startTime).hour() * 60 + dayjs(startTime).minute() + 1;
    maximumDurationInMinutes -= timeToDeduct;
  }

  let nextDate = newDate;

  while (dayjs(newDate).isSame(nextDate, 'day')) {
    const difference = dayjs(nextDate).diff(newDate, 'minutes');
    if (difference) {
      const { hours, minutes } = convertToHoursAndMinutes(difference);
      const Option = {
        label: `${hours ? translateTimeUnit('hours', hours) : ''}${
          minutes ? translateTimeUnit('minutes', minutes) : ''
        }`,
        value: difference,
        inputValue: `${hours ? `${hours} h` : ''} ${
          minutes ? `${minutes} m` : ''
        }`
      };
      Options.push(Option);
    }
    nextDate = dayjs(nextDate).add(15, 'minutes');
  }

  function getValidDurationInMinutes(input: string): number {
    // const letters = input.label.match(/[a-zA-Z]+|[0-9]+/g);
    const inputDays = input.match(/[+-]?([0-9]*[.])?[0-9]+ *(da*y*s*)+/gi);
    const inputHrs = input.match(/[+-]?([0-9]*[.])?[0-9]+ *(ho*u*r*s*)+/gi);
    const inputMins = input.match(
      /[+-]?([0-9]*[.])?[0-9]+ *(mi*n*u*t*e*s*)+/gi
    );
    let mins = 0;

    if (inputDays) {
      mins +=
        parseFloat(inputDays[0].match(/[+-]?([0-9]*[.])?[0-9]+/g)[0]) * 24 * 60;
    }

    if (inputHrs) {
      mins += parseFloat(inputHrs[0].match(/[a-zA-Z]+|[0-9]+/g)[0]) * 60;
    }

    if (inputMins) {
      mins += parseInt(inputMins[0].match(/[a-zA-Z]+|[0-9]+/g)[0]);
    }

    if (inputHrs && !inputMins) {
      const allInputNumbers = input.match(/[+-]?([0-9]*[.])?[0-9]+/g);
      const remainingNumber = allInputNumbers.filter(
        (num) => num !== inputHrs[0].match(/[a-zA-Z]+|[0-9]+/g)[0]
      )[0];

      if (remainingNumber) {
        mins += parseInt(remainingNumber);
      }
    }

    if (!inputDays && !inputHrs && !inputMins && /[a-zA-Z]+/g.test(input)) {
      const allInputNumbers = input.match(/[+-]?([0-9]*[.])?[0-9]+/g)[0];
      return Math.floor(parseFloat(allInputNumbers) * 60);
    }

    if (!/[a-zA-Z]+/g.test(input)) {
      return Math.floor(parseFloat(input) * 60);
    }

    return mins;
  }

  const generateCustomOptions = (inputHours: number) => {
    const inputHoursInMins = inputHours * 60;
    const maxMinutes = inputHoursInMins + 60;
    const generatedOptions = [];
    let value = inputHoursInMins;
    while (value < maxMinutes) {
      const hours = Math.floor(value / 60); // Floor to get whole hours
      const minutes = value % 60;
      const inputOption = {
        value: value,
        label: `${hours ? translateTimeUnit('hours', hours) : ''}${
          minutes ? translateTimeUnit('minutes', minutes) : ''
        }`,
        inputValue: `${hours ? `${hours} h` : ''} ${
          minutes ? `${minutes} m` : ''
        }`
      };
      generatedOptions.push(inputOption);
      value = value + 15;
    }
    // Add return statement
    return generatedOptions;
  };
  const filterOptions = (currentDurationInput: Option): Options => {
    let exactMatches = [];
    let relativeMatches = [];
    let resultArray = [];

    if (isEmpty(currentDurationInput?.label) || !currentDurationInput?.value) {
      setFilteredOptions(
        Options.filter((opt) => opt.value <= maximumDurationInMinutes)
      );
    } else if (currentDurationInput?.label !== durationInput?.label) {
      const {
        hours: inputHours,
        minutes: inputMinutes
      } = convertToHoursAndMinutes(currentDurationInput?.value);

      if (currentDurationInput?.value >= maximumDurationInMinutes) {
        const {
          hours: maximumDurationHours,
          minutes: maximumDurationMinutes
        } = convertToHoursAndMinutes(maximumDurationInMinutes);

        setFilteredOptions([
          {
            value: maximumDurationInMinutes,
            label: `${
              maximumDurationHours
                ? translateTimeUnit('hours', maximumDurationHours)
                : ''
            }${
              maximumDurationMinutes
                ? translateTimeUnit('minutes', maximumDurationMinutes)
                : ''
            }`,
            inputValue: `${
              maximumDurationHours ? `${maximumDurationHours} h` : ''
            } ${maximumDurationMinutes ? `${maximumDurationMinutes} m` : ''}`
          }
        ]);
        return;
      }

      for (let option of Options) {
        // Find the exact matches based on the input time(minutes)
        if (
          currentDurationInput?.value &&
          currentDurationInput?.value === option.value
        ) {
          exactMatches.push(option);

          // Find the relative matches based on the input string
        } else if (
          option?.label.includes(translateTimeUnit('hours', inputHours))
        ) {
          if (
            option.value <= maximumDurationInMinutes &&
            (!inputMinutes ||
              option?.label.includes(
                translateTimeUnit('minutes', inputMinutes)
              ))
          ) {
            relativeMatches.push(option);
          }
        }
      }

      if (exactMatches.length || relativeMatches.length) {
        resultArray = [...exactMatches, ...relativeMatches];
      }

      // Append a new option to match user input, if no exact matches in available options
      else {
        const defaultOption = {
          value: currentDurationInput?.value,
          label: `${inputHours ? translateTimeUnit('hours', inputHours) : ''}${
            inputMinutes ? translateTimeUnit('minutes', inputMinutes) : ''
          }`,
          inputValue: `${inputHours ? `${inputHours} h` : ''} ${
            inputMinutes ? `${inputMinutes} m` : ''
          }`
        };
        if (maxDuration && inputHours < maxDuration) {
          const customOptions = generateCustomOptions(inputHours);
          exactMatches = exactMatches.concat(customOptions);
          const isDefaultOptionPresent = exactMatches.find(
            (el) => el?.value === defaultOption?.value
          );
          if (!isDefaultOptionPresent) {
            exactMatches.unshift(defaultOption);
          }
        } else {
          exactMatches.unshift(defaultOption);
        }
        resultArray = [...exactMatches];
      }
      setFilteredOptions(resultArray);
    }
  };

  function handleDurationInput(input: Option): void {
    setDurationInput(input);
    filterOptions(input);
  }

  function durationInputHandler(val): void {
    const sanitizedVal = val.replace(/[^\w\s]/gi, '');
    const mins = getValidDurationInMinutes(sanitizedVal);
    if (mins >= 0) {
      handleDurationInput({
        label: val,
        value: mins,
        inputValue: val
      });
    } else {
      handleDurationInput(null);
    }
  }

  function durationSelectHandler(selectedOption: Option): void {
    handleDurationInput(selectedOption);
    onChange(selectedOption?.value);
  }

  function handleSelect(value: string): void {
    if (filteredOptions.length) {
      const selectedOption = filteredOptions.find(
        (opt) => opt?.value === parseInt(value)
      );
      if (selectedOption) {
        durationSelectHandler(selectedOption);
      }
    } else {
      durationSelectHandler(null);
    }
  }

  function handleDurationFromProps(): void {
    const { hours, minutes } = convertToHoursAndMinutes(duration);
    handleDurationInput({
      value: duration,
      label: `${
        hours ? translateTimeUnit('hours', hours) : ''
      }${translateTimeUnit('minutes', minutes)}`,
      inputValue: `${hours ? `${hours} h` : ''} ${minutes} m`
    });
  }

  // function openComboboxManually(event: KeyboardEvent) {
  //   if (
  //     comboboxRef.current?.openPopover &&
  //     (event.key === 'Enter' || event.key === 'ArrowDown')
  //   ) {
  //     comboboxRef.current?.openPopover();
  //   }
  // }

  useEffect(() => {
    if (typeof duration === 'number' && duration >= 0) {
      handleDurationFromProps();
    }
  }, [duration]);

  useEffect(() => {
    filterOptions(durationInput);
  }, [maximumDurationInMinutes]);

  // useEffect(() => {
  //   document.addEventListener('keydown', openComboboxManually);
  //   return () => {
  //     document.removeEventListener('keydown', openComboboxManually);
  //   };
  // }, []);

  return (
    <div className="fw">
      <Combobox
        refFromProps={comboboxRef}
        preferredPosition="below"
        activator={
          <Combobox.TextField
            type="text"
            value={durationInput?.inputValue}
            // placeholder={getTranslatedText(
            //   intl,
            //   'attribute.duration.placeholder'
            // )}
            placeholder={placeholder || 'eg: "1h 3m"'}
            label="Duration"
            autoComplete="off"
            onChange={durationInputHandler}
            // onFocus={(event) => {
            //   event.target.select();
            // }}
            onBlur={() => {
              if (duration && !durationInput?.label) {
                handleDurationFromProps();
              }
            }}
            noborder
            bgTransparent
            labelHidden
            disabled={disabled}
            selectValueOnFocus
          />
        }
        // active={popupActive}
        onClose={() => {
          if (durationInput?.value && durationInput?.value !== duration) {
            durationSelectHandler(filteredOptions[0]);
          }
        }}
        zIndexOverride={525}
        fullWidth={false}
      >
        <Listbox onSelect={handleSelect}>
          {filteredOptions?.length ? (
            filteredOptions.map((opt) => {
              const { label, value } = opt;
              return (
                <Listbox.Option
                  key={`${value}`}
                  value={value.toString()}
                  // selected={selectedOption === value}
                  accessibilityLabel={label}
                >
                  {label}
                </Listbox.Option>
              );
            })
          ) : (
            <FormattedMessage id="app.search.noOptions.pl" />
          )}
        </Listbox>
      </Combobox>
    </div>
  );
}

export default DurationDropdown;
